import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { affiliatorGroupApi } from '@/services/subscriptionApi';
import { useToast } from '@/hooks/useToast';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { ArrowLeft, Users, BarChart3, DollarSign } from 'lucide-react';
import type { AffiliatorStatus, CommissionModel, PaymentFrequency } from '@/types/subscription';

export const AffiliatorGroupStatsPage: React.FC = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    pending: 0,
    inactive: 0,
    suspended: 0,
    terminated: 0,
    rejected: 0,
    byStatus: {} as Record<AffiliatorStatus, number>,
    byCommissionModel: {} as Record<CommissionModel, number>,
    byPaymentFrequency: {} as Record<PaymentFrequency, number>,
    averageCommissionRate: 0,
    totalPayouts: 0,
    pendingPayouts: 0,
  });
  const { toast } = useToast();

  useEffect(() => {
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount
  }, []);

  const fetchStats = async () => {
    try {
      setIsLoading(true);
      // Fetch all affiliators (with a large page size to get all)
      const response = await affiliatorGroupApi.list({
        page: 0,
        size: 1000, // Large size to get all affiliators
      });

      const affiliators = response.content || [];

      // Calculate statistics
      const newStats = {
        total: affiliators.length,
        active: affiliators.filter((a) => a.status === 'ACTIVE').length,
        pending: affiliators.filter((a) => a.status === 'PENDING').length,
        inactive: affiliators.filter((a) => a.status === 'INACTIVE').length,
        suspended: affiliators.filter((a) => a.status === 'SUSPENDED').length,
        terminated: affiliators.filter((a) => a.status === 'TERMINATED').length,
        rejected: affiliators.filter((a) => a.status === 'REJECTED').length,
        byStatus: {} as Record<AffiliatorStatus, number>,
        byCommissionModel: {} as Record<CommissionModel, number>,
        byPaymentFrequency: {} as Record<PaymentFrequency, number>,
        averageCommissionRate: 0,
        totalPayouts: 0,
        pendingPayouts: 0,
      };

      // Count by status
      affiliators.forEach((affiliator) => {
        newStats.byStatus[affiliator.status] = (newStats.byStatus[affiliator.status] || 0) + 1;
      });

      // Count by commission model
      affiliators.forEach((affiliator) => {
        newStats.byCommissionModel[affiliator.commissionModel] =
          (newStats.byCommissionModel[affiliator.commissionModel] || 0) + 1;
      });

      // Count by payment frequency
      affiliators.forEach((affiliator) => {
        newStats.byPaymentFrequency[affiliator.paymentFrequency] =
          (newStats.byPaymentFrequency[affiliator.paymentFrequency] || 0) + 1;
      });

      // Calculate averages and totals
      if (affiliators.length > 0) {
        const percentageAffiliators = affiliators.filter((a) => a.profitShareType === 'PERCENTAGE');
        if (percentageAffiliators.length > 0) {
          const totalPercentage = percentageAffiliators.reduce((sum, a) => sum + a.profitSharePercentage, 0);
          newStats.averageCommissionRate = totalPercentage / percentageAffiliators.length;
        }
      }

      newStats.totalPayouts = affiliators.reduce((sum, a) => sum + a.totalPaidOut, 0);
      newStats.pendingPayouts = affiliators.reduce((sum, a) => sum + a.pendingPayouts, 0);

      setStats(newStats);
    } catch (error: unknown) {
      console.error('Error fetching stats:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: (error as Error)?.message ?? 'Failed to load statistics',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" asChild>
            <Link to="/admin/affiliators">
              <ArrowLeft className="h-4 w-4" />
            </Link>
          </Button>
          <div>
            <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
              <BarChart3 className="h-8 w-8" />
              Affiliator Statistics
            </h1>
            <p className="text-muted-foreground mt-2">Overview of affiliator groups</p>
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="text-center py-8">Loading statistics...</div>
      ) : (
        <>
          {/* Overview Cards */}
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-6">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Affiliators</CardTitle>
                <Users className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats.total}</div>
                <p className="text-xs text-muted-foreground">All affiliators</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Active</CardTitle>
                <Users className="h-4 w-4 text-green-600" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-green-600">{stats.active}</div>
                <p className="text-xs text-muted-foreground">Currently active</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Pending</CardTitle>
                <Users className="h-4 w-4 text-yellow-600" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-yellow-600">{stats.pending}</div>
                <p className="text-xs text-muted-foreground">Pending approval</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Avg Commission</CardTitle>
                <DollarSign className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats.averageCommissionRate.toFixed(2)}%</div>
                <p className="text-xs text-muted-foreground">Average rate</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Payouts</CardTitle>
                <DollarSign className="h-4 w-4 text-green-600" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-green-600">{formatCurrency(stats.totalPayouts)}</div>
                <p className="text-xs text-muted-foreground">All time</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Pending Payouts</CardTitle>
                <DollarSign className="h-4 w-4 text-yellow-600" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-yellow-600">{formatCurrency(stats.pendingPayouts)}</div>
                <p className="text-xs text-muted-foreground">Awaiting payment</p>
              </CardContent>
            </Card>
          </div>

          {/* Distribution by Status */}
          <Card>
            <CardHeader>
              <CardTitle>Distribution by Status</CardTitle>
              <CardDescription>Number of affiliators per status</CardDescription>
            </CardHeader>
            <CardContent>
              {Object.keys(stats.byStatus).length > 0 ? (
                <div className="space-y-4">
                  {Object.entries(stats.byStatus)
                    .sort(([, a], [, b]) => b - a)
                    .map(([status, count]) => (
                      <div key={status} className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{status}</span>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="w-32 bg-secondary rounded-full h-2">
                            <div
                              className="bg-primary h-2 rounded-full"
                              style={{
                                width: `${(count / stats.total) * 100}%`,
                              }}
                            />
                          </div>
                          <span className="text-sm font-medium w-12 text-right">{count}</span>
                        </div>
                      </div>
                    ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">No affiliators found</div>
              )}
            </CardContent>
          </Card>

          {/* Distribution by Commission Model */}
          <Card>
            <CardHeader>
              <CardTitle>Distribution by Commission Model</CardTitle>
              <CardDescription>Number of affiliators per commission model</CardDescription>
            </CardHeader>
            <CardContent>
              {Object.keys(stats.byCommissionModel).length > 0 ? (
                <div className="space-y-4">
                  {Object.entries(stats.byCommissionModel)
                    .sort(([, a], [, b]) => b - a)
                    .map(([model, count]) => (
                      <div key={model} className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{model.replace(/_/g, ' ')}</span>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="w-32 bg-secondary rounded-full h-2">
                            <div
                              className="bg-primary h-2 rounded-full"
                              style={{
                                width: `${(count / stats.total) * 100}%`,
                              }}
                            />
                          </div>
                          <span className="text-sm font-medium w-12 text-right">{count}</span>
                        </div>
                      </div>
                    ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">No affiliators found</div>
              )}
            </CardContent>
          </Card>

          {/* Distribution by Payment Frequency */}
          <Card>
            <CardHeader>
              <CardTitle>Distribution by Payment Frequency</CardTitle>
              <CardDescription>Number of affiliators per payment frequency</CardDescription>
            </CardHeader>
            <CardContent>
              {Object.keys(stats.byPaymentFrequency).length > 0 ? (
                <div className="space-y-4">
                  {Object.entries(stats.byPaymentFrequency)
                    .sort(([, a], [, b]) => b - a)
                    .map(([frequency, count]) => (
                      <div key={frequency} className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{frequency}</span>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="w-32 bg-secondary rounded-full h-2">
                            <div
                              className="bg-primary h-2 rounded-full"
                              style={{
                                width: `${(count / stats.total) * 100}%`,
                              }}
                            />
                          </div>
                          <span className="text-sm font-medium w-12 text-right">{count}</span>
                        </div>
                      </div>
                    ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">No affiliators found</div>
              )}
            </CardContent>
          </Card>

          {/* Performance Metrics Placeholder */}
          <Card>
            <CardHeader>
              <CardTitle>Performance Metrics</CardTitle>
              <CardDescription>Top performers and conversion rates</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-center py-8 text-muted-foreground">
                <p>Performance metrics will be available when backend metrics are enabled.</p>
                <p className="text-sm mt-2">
                  This feature requires backend metrics collection to be configured.
                </p>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export default AffiliatorGroupStatsPage;
